// Copyright ©️ 2026 eVolve MEP, LLC

import { createContext, type FocusEvent, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import {
  type BendCreatorValidationRequest,
  bendInputKeys,
  type BendValidationResponse,
  extractErrorMessage,
  isCanceledApiRequest,
  type StrongExclude,
  useEvolvePost,
  type YBCRow,
} from '@evolvemep/api-ts';
import type { GetInputPropsOptions, GetInputPropsReturnType } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { isKeyOf } from 'helpers/isKeyOf';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { strongEntries } from 'helpers/strongEntries';
import { useGeneralContext } from 'helpers/useGeneralContext';
import {
  getBendTypeWithYBCRows,
  withoutYbcRowErrors,
} from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/helpers';
import {
  type BendCreatorFormValues,
  bendInputDefaults,
  useBendCreatorFormContext,
} from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorFormContext';

type YBCRowPath = `ybcRows.${number}.${keyof YBCRow}`;

type BendCreatorValidationContextValue = {
  /** Validates the bend with the backend. */
  validateBend: <T extends StrongExclude<keyof BendCreatorFormValues, 'partName' | 'quantity'>>(
    /** Optional. If provided, indicates what field changed to trigger validation. */
    changingKey?: T,
    /** Optional. if provided, indicates the new value of the field that changed to trigger validation. */
    changingValue?: BendCreatorFormValues[T],
  ) => Promise<void>;
  /** Wrapper around getInputProps that performs serverside validation. */
  wrappedGetInputProps: <T extends StrongExclude<keyof BendCreatorFormValues, 'partName' | 'quantity'>>(
    path: T,
    options?: GetInputPropsOptions & {
      /** When true, the input will perform backend validation on change. If unset or false, the input will validate on blur. */
      validateOnChange?: boolean;
    },
  ) => GetInputPropsReturnType;
  /** Wrapper around getInputProps for a YBC row cell that performs serverside validation on blur. */
  wrappedGetYBCRowInputProps: (path: YBCRowPath) => GetInputPropsReturnType;
  isValidating: boolean;
  lastValidation: LastValidationState | null;
  /** Resets the validation state to null/not yet validated. Generally, if you did something that would invalidate the form (like blanking a field), you should call this. */
  resetLastValidation: () => void;
};

const BendCreatorValidationContextProvider = createContext<BendCreatorValidationContextValue | undefined>(undefined);

/**
 * This represents a backend error with a dictionary of field names to error messages.
 * If we get this kind of error we can use the values to set field errors on the form.
 */
type ServiceValidationError = {
  title: string;
  status: number;
  errors: Record<keyof BendCreatorValidationRequest, string[]>;
  traceId: string;
};

type LastValidationState = {
  request: BendCreatorValidationRequest;
} & (
  | {
      response: BendValidationResponse;
      isSuccess: true;
    }
  | {
      response: unknown;
      isSuccess: false;
    }
);

/**
 * If we have an HTTP400 error, assume it's a ServiceValidationError.
 */
const isServiceValidationError = (e: unknown): e is { response: { data: ServiceValidationError } } => {
  if (isNil(e)) return false;
  if (typeof e !== 'object') return false;
  return 'status' in e && e.status === 400;
};

export const BendCreatorValidationContext = ({
  children,
  validateOnMount,
}: {
  children: ReactNode;
  validateOnMount?: boolean;
}) => {
  const form = useBendCreatorFormContext();
  const {
    apiCall: requestValidation,
    loading: isValidating,
    abortAll,
  } = useEvolvePost('shop/fabrication/bendCreator/validate', {
    alertOnError: false,
    abortOnMoreRecent: true,
  });

  const [lastValidation, setLastValidation] = useState<LastValidationState | null>(null);
  const {
    values,
    getValues,
    setFieldValue,
    setFieldError,
    setErrors,
    clearFieldError,
    getInputProps,
    isValid,
    validateField,
  } = form;

  const prepareValues = useCallback(
    <T extends StrongExclude<keyof BendCreatorFormValues, 'partName' | 'quantity'>>(
      changingKey?: T,
      changingValue?: BendCreatorFormValues[T],
    ): BendCreatorValidationRequest | null => {
      const {
        partName: _,
        quantity: __,
        benderProfile,
        conduitTypeId,
        conduitSizeId,
        bendType,
        materialStockLength,
        ybcRows,
        ...rest
      } = getValues();

      /* Don't try to perform server side validation until
       * required fields are present, some bend dimension value isn't blank, and the user's input is valid clientside. */
      if (
        isNil(benderProfile) ||
        isNil(conduitTypeId) ||
        isNil(conduitSizeId) ||
        isNil(bendType) ||
        isNil(materialStockLength)
      ) {
        return null;
      }
      const bendTypeWithRows = getBendTypeWithYBCRows({ bendType, ybcRows });
      if (isNil(bendTypeWithRows)) return null;
      if (bendType === 'Custom') {
        if (!isValid('ybcRows')) return null;
      } else if (!bendInputKeys.some((key) => isNotNil(rest[key]))) {
        return null;
      }
      if (bendInputKeys.some((key) => !isValid(key))) return null;

      /* Some special case handling for angled bends, which can be overdefined.
       * Treat the possibly overdefined value that the user didn't touch as null.
       * It will be updated post-validation */
      if (bendType === 'Angled') {
        if (changingKey === 'kickHeight') {
          rest.centerToEnd1 = null;
        } else {
          rest.kickHeight = null;
        }
      }

      return {
        ...rest,
        benderProfileId: benderProfile.machineProfileId,
        conduitTypeId,
        conduitSizeId,
        materialStockLength,
        ...bendTypeWithRows,
        /** If the changing key and value are provided, use them instead of what's in `rest`. */
        ...(isNotNil(changingKey) && isNotNil(changingValue) ? { [changingKey]: changingValue } : {}),
      };
    },
    [getValues, isValid],
  );

  // On success, update the form with values from the backend
  const handleSuccess = useCallback(
    (res: Awaited<ReturnType<typeof requestValidation>>) => {
      clearFieldError('bendType');
      // A success means the backend accepted every row, so a row error it set on an earlier request is stale.
      setErrors(withoutYbcRowErrors);
      res.updatedBend.forEach(({ keyName, value }) => {
        setFieldValue(keyName, value);
        clearFieldError(keyName);
      });
    },
    [setFieldValue, setErrors, clearFieldError],
  );

  // On error, if possible, update the form with errors, otherwise show a notification.
  const handleError = useCallback(
    (e: unknown) => {
      if (isServiceValidationError(e)) {
        const {
          response: {
            data: { errors },
          },
        } = e;
        // Put any errors that are not related to a given field on the "bend type" field.
        const generalErrors: string[] = [];
        strongEntries(errors).forEach(([key, errorMessages]) => {
          // The backend keys a row's errors as `ybcRows[1].angle`; the form path for that field is `ybcRows.1.angle`.
          const ybcRowPath = key.replace(/^ybcRows\[(\d+)\]/, 'ybcRows.$1');
          if (ybcRowPath.startsWith('ybcRows.')) {
            setFieldError(ybcRowPath, errorMessages.join(', '));
          } else if (isKeyOf(key, values)) {
            if (key === 'bendType') {
              generalErrors.push(...errorMessages);
            } else {
              setFieldError(key, errorMessages.join(', '));
            }
          } else {
            generalErrors.push(...errorMessages);
          }
        });
        if (generalErrors.length > 0) {
          setFieldError('bendType', generalErrors.join(', '));
        }
      } else {
        notifications.show({
          title: 'Error validating bend',
          message: `An unexpected error occurred while validating bend: ${extractErrorMessage(e)}`,
          color: 'red',
        });
      }
    },
    [values, setFieldError],
  );

  const resetLastValidation = useCallback(() => {
    // A request still in flight would otherwise land after the reset and restore a stale result.
    abortAll('Validation reset');
    setLastValidation(null);
  }, [abortAll]);

  /** Validate the bend and update the form with the results. */
  const validateBend = useCallback(
    async <T extends StrongExclude<keyof BendCreatorFormValues, 'partName' | 'quantity'>>(
      changingKey?: T,
      changingValue?: BendCreatorFormValues[T],
    ) => {
      const validationValues = prepareValues(changingKey, changingValue);
      if (isNil(validationValues)) {
        // Any previous result described a form state that no longer exists.
        resetLastValidation();
        return;
      }

      try {
        const res = await requestValidation(validationValues);
        handleSuccess(res);
        setLastValidation({ request: validationValues, response: res, isSuccess: true });
      } catch (e) {
        if (isCanceledApiRequest(e)) return;
        handleError(e);
        setLastValidation({ request: validationValues, response: e, isSuccess: false });
      }
    },
    [requestValidation, prepareValues, handleSuccess, handleError, resetLastValidation],
  );

  const wrappedGetInputProps = useCallback(
    <T extends StrongExclude<keyof BendCreatorFormValues, 'partName' | 'quantity'>>(
      path: T,
      options?: GetInputPropsOptions & {
        /** When true, the input will perform backend validation on change. If unset or false, the input will validate on blur. */
        validateOnChange?: boolean;
      },
    ) => {
      const { onBlur: internalOnBlur, onChange: internalOnChange, ...rest } = getInputProps(path, options);
      const onBlur = (e: FocusEvent<HTMLInputElement>) => {
        // mantine use-form's typing upsets me.
        // oxlint-disable-next-line typescript/no-unsafe-call
        internalOnBlur?.(e);
        if (options?.validateOnChange) return;
        /* Surface the clientside message for a bad bend dimension, which `validateBend` won't send to the backend.
         * Only when the value is invalid: `validateField` clears the field's error when its rule passes,
         * which would wipe an error the backend set. */
        if (isKeyOf(path, bendInputDefaults) && !isValid(path)) {
          validateField(path);
        }
        void validateBend(path);
      };
      const onChange = (newValue: BendCreatorFormValues[T] | null) => {
        // oxlint-disable-next-line typescript/no-unsafe-call
        internalOnChange?.(newValue);
        if (isNotNil(newValue) && options?.validateOnChange) {
          void validateBend(path, newValue);
        }
      };

      return {
        ...rest,
        onBlur,
        onChange,
        ...(isValidating ? { readOnly: true } : {}),
      };
    },
    [isValidating, validateBend, getInputProps, isValid, validateField],
  );

  // Not read-only while validating: custom validation writes nothing back, and locking would swallow keystrokes.
  const wrappedGetYBCRowInputProps = useCallback(
    (path: YBCRowPath) => {
      const { onBlur: internalOnBlur, ...rest } = getInputProps(path);
      const onBlur = (e: FocusEvent<HTMLInputElement>) => {
        // mantine use-form's typing upsets me (onBlur is typed as `any`)
        // oxlint-disable-next-line typescript/no-unsafe-call
        internalOnBlur?.(e);
        if (!isValid(path)) validateField(path);
        void validateBend();
      };
      return { ...rest, onBlur };
    },
    [validateBend, getInputProps, isValid, validateField],
  );

  const shouldDoInitialValidation = useRef(validateOnMount ?? false);
  useEffect(() => {
    if (shouldDoInitialValidation.current) {
      void validateBend();
      shouldDoInitialValidation.current = false;
    }
  }, [validateBend]);

  const value = {
    validateBend,
    wrappedGetInputProps,
    wrappedGetYBCRowInputProps,
    isValidating,
    lastValidation,
    resetLastValidation,
  };
  return <BendCreatorValidationContextProvider value={value}>{children}</BendCreatorValidationContextProvider>;
};

export const useBendCreatorValidationContext = () =>
  useGeneralContext(BendCreatorValidationContextProvider, 'BendCreatorValidationContext');
